import { type ReactNode } from 'react';

import type {
  ConvertibleUnit,
  FormatOptions,
  Unit,
} from '@dynatrace-sdk/units';

import type { BehaviorTrackingProps } from '../../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../../core/types/data-props.js';
import type { MaskingProps } from '../../../core/types/masking-props.js';
import type { StylingProps } from '../../../core/types/styling-props.js';
import type { TruncationMode } from '../../../typography/text-ellipsis/TextEllipsis.js';
import type { ChartData } from '../../core/types/chart-data.js';
import type { ChartUnit } from '../../core/types/chart-unit.js';
import type {
  ColorPalette,
  CustomColorPalette,
} from '../../core/types/color-palette.js';
import type { ColorRuleProps } from '../../core/types/color-rule.js';
import type { Formatter } from '../../core/types/formatter.js';
import type { LegendProps } from '../../core/types/legend.js';
import type { SeriesActionsTemplate } from '../../core/types/series-actions-template.js';
import {
  PieChartInternalToolbarConfig,
  PieChartToolbarConfig,
} from './toolbar.js';
/**
 * Unit for pie/donut chart slices.
 * @public
 * @deprecated use string or Unit type instead
 */
export type SliceUnit = string;

/**
 * Legacy data format for a single pie/donut slice.
 * @public
 * @deprecated Use the unified `ChartData` (`ChartDatapoint[]`) format together
 * with `labelAccessor`/`valueAccessor` instead. This interface will be removed
 * in a future release.
 */
export interface SliceData {
  /** The category of the slice*/
  category: string;
  /** The value of the slice */
  value: number;
  /**
   * The color of the slice. Overrides the color assigned from the color palette.
   *
   * @deprecated This key will be removed in a future release. Use ColorRule slot instead.
   */
  color?: string;
}

/**
 * Legacy data format for PieChart slices.
 * @public
 * @deprecated Use the unified `ChartData` (`ChartDatapoint[]`) format together
 * with `labelAccessor`/`valueAccessor` and the root-level `unit` prop instead.
 * This interface will be removed in a future release.
 */
export interface PieChartData {
  /** Slice data array */
  // oxlint-disable-next-line typescript/no-deprecated
  slices: SliceData[];
  /** The unit of the slices */
  unit?: string | Unit;
}

/**
 * Properties shared by both the unified `ChartData` and the legacy
 * `PieChartData` variants of {@link PieChartProps}.
 * @public
 */
export interface PieChartBaseProps
  extends StylingProps, MaskingProps, DataTestId, BehaviorTrackingProps {
  /**
   * The width of the chart. A number or a string with accepted CSS units is expected.
   * @defaultValue 100%
   */
  width?: number | string;
  /** The height of the chart. If a number is passed, it will be treated as px.
   * @defaultValue 300px.
   */
  height?: number | string;
  /**
   * Set of Color palette to be used in charts.
   * @defaultValue 'categorical'.
   */
  colorPalette?: ColorPalette | CustomColorPalette;
  /**
   * Custom Formatter for the chart
   */
  formatter?: Formatter | FormatOptions<Unit, ConvertibleUnit>;
  /**
   * Truncation mode to use (start, middle, end)
   * Applied to all the parts that truncate text.
   * @defaultValue 'middle'
   */
  truncationMode?: TruncationMode;
  /** Exposed callback to display series actions for a slice  */
  // oxlint-disable-next-line typescript/no-deprecated
  seriesActions?: (slice: SliceData) => SeriesActionsTemplate;
  /** Show the loading indicator when truly.
   * @defaultValue false
   */
  loading?: boolean;
}

/**
 * PieChart props using the unified `ChartData` format. The slice label and
 * value are resolved from each datapoint through `labelAccessor` and
 * `valueAccessor` (dot-notation is supported, e.g. `"metrics.value"`).
 * @public
 */
export interface PieChartAccessorDataProps extends PieChartBaseProps {
  /**
   * Categorical data of the chart in the unified `ChartData` format.
   */
  data: ChartData;
  /** Accessor describing the field that holds the slice label/category. */
  labelAccessor: string;
  /** Accessor describing the field that holds the slice value. */
  valueAccessor: string;
  /** The unit of the slices. */
  unit?: ChartUnit;
}

/**
 * PieChart props using the legacy `PieChartData` format.
 * @public
 * @deprecated Use the unified `ChartData` format with
 * `labelAccessor`/`valueAccessor` instead (see {@link PieChartAccessorDataProps}).
 */
export interface PieChartLegacyDataProps extends PieChartBaseProps {
  /** Categorical data of the chart.*/
  // oxlint-disable-next-line typescript/no-deprecated
  data: PieChartData;
  /** Not available in the legacy data format. Use `PieChartAccessorDataProps` with the unified `ChartData` format. */
  labelAccessor?: never;
  /** Not available in the legacy data format. Use `PieChartAccessorDataProps` with the unified `ChartData` format. */
  valueAccessor?: never;
  /** Not available in the legacy data format. Set the unit via `PieChartData.unit` instead. */
  unit?: never;
}

/**
 * Props for the PieChart component.
 * @public
 */
// oxlint-disable-next-line typescript/no-deprecated
export type PieChartProps = PieChartAccessorDataProps | PieChartLegacyDataProps;

/**
 * Configuration for the PieChart context, allowing shared config across multiple charts via {@link PieChartConfig}.
 * @public
 */
export interface PieChartContextConfig {
  /**
   * Set of Color palette to be used in charts.
   * @defaultValue 'categorical'.
   */
  colorPalette?: ColorPalette | CustomColorPalette;
  /**
   * Config for the labels of the pie chart slices.
   */
  labels?: LabelsConfig;
  /**
   * Group configuration for the pie chart.
   * Aggregates multiple slices into a group (matches threshold config).
   */
  grouping?: GroupConfig;
  /**
   * Legend configuration for the pie chart
   */
  legend?: PieChartLegendConfig;
  /**
   * Custom Formatter for the chart
   */
  formatter?: Formatter | FormatOptions<Unit, ConvertibleUnit>;
  /**
   * Truncation mode to use (start, middle, end)
   * Applied to all the parts that truncate text.
   * @defaultValue 'middle'
   */
  truncationMode?: TruncationMode;
  /**
   * Toolbar configuration
   */
  toolbar?: PieChartToolbarConfig;
  /**
   * Tooltip configuration
   */
  tooltip?: PieChartCustomTooltipConfig;
  /** Callback to display series actions for a slice */
  // oxlint-disable-next-line typescript/no-deprecated
  seriesActions?: (slice: SliceData) => SeriesActionsTemplate;
  colorRules?: ColorRuleProps[];
}

/**
 * @internal
 */
export type DonutChartContextConfig = PieChartContextConfig;

/**
 * Full configuration for the PieChart component, extending {@link PieChartContextConfig} with chart dimensions.
 * @public
 */
export interface PieChartConfiguration extends PieChartContextConfig {
  /** The height of the chart. If a number is passed, it will be treated as px.
   * @defaultValue 300px.
   */
  height?: number | string;
}

/**
 * @internal
 */
export type DonutChartConfiguration = PieChartConfiguration;

// Config type holding all the required config used in the pie chart. Considers that the defaults have already been applied.
/**
 * @internal
 */
export type RequiredPieChartConfiguration = Required<
  Omit<
    PieChartConfiguration,
    'toolbar' | 'seriesActions' | 'colorRules' | 'tooltip'
  >
> & {
  toolbar?: PieChartInternalToolbarConfig;
  tooltip?: PieChartCustomTooltipConfig;
  innerElement?: InnerHandler | ReactNode;
  colorRules?: ColorRuleProps[];
};

/**
 * @internal
 */
export type RequiredDonutChartConfiguration = RequiredPieChartConfiguration;

/**
 * Tooltip configuration for the pie chart
 * @public
 */
export type PieChartCustomTooltipConfig = {
  hidden?: boolean;
  template?: PieChartCustomTooltipProps['children'];
};

/**
 * @internal
 */
export type NotRequiredPieChartConfiguration = {
  // oxlint-disable-next-line typescript/no-deprecated
  seriesActions?: (slice: SliceData) => SeriesActionsTemplate;
};

/**
 * @internal
 */
export type NotRequiredDonutChartConfiguration =
  NotRequiredPieChartConfiguration;

/**
 * @internal
 */
export type PieDonutConfig = (
  | RequiredPieChartConfiguration
  | RequiredDonutChartConfiguration
) &
  (NotRequiredPieChartConfiguration | NotRequiredDonutChartConfiguration);

/**
 * @public
 */
export type LabelTypeOptions = 'absolute' | 'relative';
/**
 * @public
 */
export interface LabelsConfig {
  /**
   * Hides the slice labels.
   * @defaultValue false (all the labels are displayed).
   */
  hidden?: boolean;
  /**
   * Display the label with a relative or absolute value.
   * @defaultValue 'relative'
   */
  valueType?: LabelTypeOptions;
}

/**
 * @public
 */
export const thresholdTypeOptions = [
  'absolute',
  'relative',
  'number-of-slices',
] as const;
/**
 * @public
 */
export type ThresholdType = (typeof thresholdTypeOptions)[number];

/**
 * @public
 */
export interface ThresholdConfig {
  /**
   * Type of the grouping threshold. Can be "relative", "absolute" or "number-of-slices".
   * @defaultValue 'relative'.
   */
  type: ThresholdType;
  /**
   * Value of the group threshold.
   * Default is dynamically calculated depending on the chart size for relative and absolute. 10 in case of number-of-slices.
   */
  value: number;
}

/**
 * @public
 */
export interface GroupConfig {
  /**
   * Name of the group.
   * @defaultValue 'Other'.
   */
  name?: string;
  /**
   * The threshold for the group.
   */
  threshold: ThresholdConfig;
}

/**
 * Legend configuration for the PieChart component.
 * @public
 */
export type PieChartLegendConfig = LegendProps;

/**
 * Legacy data format for the DonutChart component.
 * @public
 * @deprecated Use the unified `ChartData` (`ChartDatapoint[]`) format together
 * with `labelAccessor`/`valueAccessor` and the root-level `unit` prop instead.
 * This type will be removed in a future release.
 */
// oxlint-disable-next-line typescript/no-deprecated
export type DonutChartData = PieChartData;

/**
 * Properties shared by both the unified `ChartData` and the legacy
 * `DonutChartData` variants of {@link DonutChartProps}.
 * @public
 */
export type DonutChartBaseProps = PieChartBaseProps;

/**
 * DonutChart props using the unified `ChartData` format. The slice label and
 * value are resolved from each datapoint through `labelAccessor` and
 * `valueAccessor` (dot-notation is supported, e.g. `"metrics.value"`).
 * @public
 */
export interface DonutChartAccessorDataProps extends DonutChartBaseProps {
  /**
   * Categorical data of the chart in the unified `ChartData` format.
   */
  data: ChartData;
  /** Accessor describing the field that holds the slice label/category. */
  labelAccessor: string;
  /** Accessor describing the field that holds the slice value. */
  valueAccessor: string;
  /** The unit of the slices. */
  unit?: ChartUnit;
}

/**
 * DonutChart props using the legacy `DonutChartData` format.
 * @public
 * @deprecated Use the unified `ChartData` (`ChartDatapoint[]`) format together
 * with `labelAccessor`/`valueAccessor` and the root-level `unit` prop instead.
 * This interface will be removed in a future release.
 */
export interface DonutChartLegacyDataProps extends DonutChartBaseProps {
  /** Categorical data of the chart. */
  // oxlint-disable-next-line typescript/no-deprecated
  data: DonutChartData;
  /** Not available in the legacy data format. Use `DonutChartAccessorDataProps` with the unified `ChartData` format. */
  labelAccessor?: never;
  /** Not available in the legacy data format. Use `DonutChartAccessorDataProps` with the unified `ChartData` format. */
  valueAccessor?: never;
  /** Not available in the legacy data format. Set the unit via `DonutChartData.unit` instead. */
  unit?: never;
}

/**
 * Props for the DonutChart component.
 * @public
 */
export type DonutChartProps =
  | DonutChartAccessorDataProps
  // oxlint-disable-next-line typescript/no-deprecated
  | DonutChartLegacyDataProps;

/**
 * Legend configuration for the DonutChart component.
 * @public
 */
export type DonutChartLegendConfig = PieChartLegendConfig;

/**
 * Data provided to the custom tooltip render function for PieChart and DonutChart.
 * @public
 */
export type PieChartTooltipPayload = {
  /** The slice category name */
  category: string;
  /** Formatted absolute value */
  value: string | number;
  /** Relative value in percent (e.g. 24.5 for 24.5%) */
  percentage: number;
  /** Slice color (hex string) */
  color: string;
};

/**
 * Props for a custom tooltip rendered inside the PieChart.
 * @public
 */
export type PieChartCustomTooltipProps = {
  hidden?: boolean;
  symbolAlignment?: 'left' | 'right';
  children?: (payload: PieChartTooltipPayload) => ReactNode;
};

/**
 * Props for a custom tooltip rendered inside the DonutChart.
 * @public
 */
export type DonutChartCustomTooltipProps = PieChartCustomTooltipProps;

// TODO: ADES-3843 Split this type into two different ones, PieChartSlots and DonutChartSlots
/**
 * @internal
 */
export interface PieChartSlots {
  labels: Partial<LabelsConfig>;
  grouping: Partial<GroupConfig>;
  legend: Partial<PieChartLegendConfig>;
  toolbar: Partial<PieChartToolbarConfig> | undefined;
  innerElement?: InnerHandler | ReactNode;
  colorRules: ColorRuleProps[];
  tooltip?: PieChartCustomTooltipConfig;
}

/**
 * @internal
 */
export type DonutChartSlots = PieChartSlots;

/**
 * @public
 */
export type InnerHandler = (value: InnerParams) => ReactNode;

/**
 * @public
 */
export type InnerParams = {
  absoluteValue: number;
  relativeValue: number;
  dimensions: { width: number; height: number };
};
